import React, { useEffect, useMemo, useState } from "react";
import { Modal, Form, Input, Select, Button, Divider } from "antd";
import { useTranslation } from "react-i18next";
import type {
  DashboardCardWithData,
  DashboardColumnApi,
} from "../../../types/types";

type Props = {
  open: boolean;
  card: DashboardCardWithData | null;
  columns: DashboardColumnApi[];
  loading?: boolean;
  onClose: () => void;
  onSave: (payload: Record<string, any>) => Promise<void> | void;
};

const DATA_CARD_TYPES = [
  "bar_chart", "line_chart", "pie_chart", "donut_chart", "scatter_plot",
  "area_chart", "histogram", "heatmap", "treemap", "word_cloud",
  "geographic_cluster_map", "trend_anomaly", "kpi_metric", "progress",
  "table", "critical_metric_snapshot",
];

const EditCardModal: React.FC<Props> = ({
  open,
  card,
  columns,
  loading = false,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation("dashboardModals");
  const [title, setTitle] = useState("");
  const [textContent, setTextContent] = useState("");
  const [imagePath, setImagePath] = useState("");
  const [selectedMetric, setSelectedMetric] = useState<string | undefined>();
  const [selectedDimension, setSelectedDimension] = useState<string | undefined>();
  const [selectedFilterColumn, setSelectedFilterColumn] = useState<string | undefined>();
  const [selectedFilterValues, setSelectedFilterValues] = useState<string[]>([]);

  const isDataCard = card ? DATA_CARD_TYPES.includes(card.card_type || "") : false;

  useEffect(() => {
    if (!card) {
      setTitle("");
      setTextContent("");
      setImagePath("");
      setSelectedMetric(undefined);
      setSelectedDimension(undefined);
      setSelectedFilterColumn(undefined);
      setSelectedFilterValues([]);
      return;
    }

    setTitle(card.title || "");
    setTextContent(card.text_content || "");
    setImagePath(card.image_path || "");
    setSelectedMetric(card.metrics?.[0]?.column ?? undefined);
    setSelectedDimension(card.dimensions?.[0]?.column ?? undefined);
    setSelectedFilterColumn(undefined);
    setSelectedFilterValues([]);
  }, [card]);

  const numericOptions = useMemo(() =>
    columns
      .filter((c) =>
        ["int64","float64","number","integer","int32","float32","float","double","numeric","decimal"]
          .includes(String(c.dtype || "").toLowerCase())
      )
      .map((c) => ({ label: c.name, value: c.name })),
    [columns]
  );

  const allColumnOptions = useMemo(() =>
    columns.map((c) => ({ label: c.name, value: c.name })),
    [columns]
  );

  const filterValueOptions = useMemo(() => {
    const found = columns.find((c) => c.name === selectedFilterColumn);
    return (found?.unique_values || []).map((v: any) => ({
      label: String(v),
      value: String(v),
    }));
  }, [columns, selectedFilterColumn]);

  const handleSubmit = async () => {
    if (!card) return;

    const payload: Record<string, any> = { title };

    if (card.card_type === "text_box") {
      payload.text_content = textContent;
    } else if (card.card_type === "image") {
      payload.image_path = imagePath;
    }

    if (isDataCard) {
      const existingAggregation = card.metrics?.[0]?.aggregation || "sum";
      payload.metrics = selectedMetric
        ? [{ name: selectedMetric, column: selectedMetric, aggregation: existingAggregation }]
        : [];
      payload.dimensions = selectedDimension
        ? [{ name: selectedDimension, column: selectedDimension }]
        : [];
      if (selectedFilterColumn && selectedFilterValues.length > 0) {
        payload.filter_column = selectedFilterColumn;
        payload.filter_values = selectedFilterValues;
      }
    }

    await onSave(payload);
  };

  return (
    <Modal
      open={open}
      title={t("editCardModal.title")}
      onCancel={onClose}
      footer={[
        <Button
          key="cancel"
          onClick={onClose}
          style={{
            color: "#4A1C96",
            border: "1px solid #4A1C96",
            height: "44px",
            fontFamily: "Poppins",
            fontSize: "16px",
          }}
        >
          {t("editCardModal.cancel")}
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
          className="save-button"
        >
          {t("editCardModal.save")}
        </Button>,
      ]}
      destroyOnClose
      className="edit-card-style"
      width={520}
    >
      {!card ? null : (
        <Form layout="vertical">
          <Form.Item label={t("editCardModal.titleLabel")}>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </Form.Item>

          {card.card_type === "text_box" && (
            <Form.Item label={t("editCardModal.textContentLabel")}>
              <Input.TextArea
                value={textContent}
                onChange={(e) => setTextContent(e.target.value)}
                autoSize={{ minRows: 4 }}
              />
            </Form.Item>
          )}

          {card.card_type === "image" && (
            <Form.Item label={t("editCardModal.imagePathLabel")}>
              <Input
                value={imagePath}
                onChange={(e) => setImagePath(e.target.value)}
                placeholder="/uploads/dashboard_images/..."
              />
            </Form.Item>
          )}

          {isDataCard && (
            <>
              <Divider style={{ margin: "12px 0" }} />

              <Form.Item label={t("editCardModal.metricLabel")}>
                <Select
                  allowClear
                  placeholder={t("editCardModal.selectMetricPlaceholder")}
                  options={numericOptions}
                  value={selectedMetric}
                  onChange={setSelectedMetric}
                />
              </Form.Item>

              <Form.Item label={t("editCardModal.dimensionLabel")}>
                <Select
                  allowClear
                  placeholder={t("editCardModal.selectDimensionPlaceholder")}
                  options={allColumnOptions}
                  value={selectedDimension}
                  onChange={setSelectedDimension}
                />
              </Form.Item>

              <Form.Item label={t("editCardModal.filterColumnLabel")}>
                <Select
                  allowClear
                  placeholder={t(
                    "editCardModal.selectFilterColumnPlaceholder",
                  )}
                  options={allColumnOptions}
                  value={selectedFilterColumn}
                  onChange={(v) => {
                    setSelectedFilterColumn(v);
                    setSelectedFilterValues([]);
                  }}
                />
              </Form.Item>

              {selectedFilterColumn && (
                <Form.Item label={t("editCardModal.filterValuesLabel")}>
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder={t(
                      "editCardModal.selectFilterValuesPlaceholder",
                    )}
                    options={filterValueOptions}
                    value={selectedFilterValues}
                    onChange={setSelectedFilterValues}
                  />
                </Form.Item>
              )}
            </>
          )}
        </Form>
      )}
    </Modal>
  );
};

export default React.memo(EditCardModal);
